import './notes-button.scss';

import {
  NoteTargetAreaType,
  NoteTargetName,
  NoteTargetType,
  useGetNotes,
} from '@converge/graphql';
import {
  SaveOutlined,
  SpeakerNotes,
  SpeakerNotesOff,
  SpeakerNotesOffOutlined,
  SpeakerNotesOutlined,
} from '@mui/icons-material';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { ConvergeTooltip } from '../../../../presentation/components/converge-tooltip';
import { IconButton } from '@mui/material';
import { RaterQuoteSaveDialog } from './rater.quote.save.dialog';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface IRaterNotesButtonProps {
  uuid: string;
  color?: string;
  targetName?: NoteTargetName;
  targetType?: NoteTargetType;
  targetArea?: NoteTargetAreaType;
  openUuid?: string;
  label?: string[];
}

export const RaterNotesButton = ({
  uuid,
  color,
  targetName,
  targetType,
  targetArea,
  openUuid,
  label,
}: IRaterNotesButtonProps) => {
  const { t } = useTranslation();
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const { dispatch, actions } = useRaterActionDispatchContext();
  const expandNotesForUuid = useRaterStateContextSelector(
    ({ expandNotesForUuid }) => {
      return expandNotesForUuid;
    }
  );
  const { notes, hasNotes } = useGetNotes({
    targetUuid: uuid,
    targetArea,
    label,
  });
  const isOpen = openUuid
    ? expandNotesForUuid === openUuid
    : expandNotesForUuid === uuid;
  const mustSave = !uuid;

  return (
    <>
      <ConvergeTooltip
        title={
          mustSave
            ? 'Save to add a note'
            : `${isOpen ? 'Hide' : 'Show'} ${
                hasNotes ? pluralize('note', notes.length, true) : 'notes'
              }`
        }
      >
        <IconButton
          onClick={() => {
            if (mustSave) {
              setOpenSaveDialog(true);
              return;
            }

            if (isOpen) {
              dispatch(actions.collapseNotes());
              return;
            }

            if (openUuid) {
              dispatch(actions.expandNotesForUuid(openUuid));
            } else {
              dispatch(actions.expandNotesForUuid(uuid));
            }
          }}
          sx={{
            color,
          }}
        >
          {!uuid ? (
            <SaveOutlined />
          ) : isOpen ? (
            hasNotes ? (
              <SpeakerNotesOff className="evidence-notes-icon" />
            ) : (
              <SpeakerNotesOffOutlined className="evidence-notes-icon" />
            )
          ) : hasNotes ? (
            <SpeakerNotes className="evidence-notes-icon" />
          ) : (
            <SpeakerNotesOutlined className="evidence-notes-icon" />
          )}
        </IconButton>
      </ConvergeTooltip>
      <RaterQuoteSaveDialog
        show={openSaveDialog}
        title={t('underwriting.quote.saveDialog.title', 'Save quote')}
        content={t(
          'underwriting.quote.saveDialog.content',
          'Please save your changes before adding a note'
        )}
        onConfirm={() => {
          setOpenSaveDialog(false);
        }}
        onCancel={() => {
          setOpenSaveDialog(false);
        }}
      />
    </>
  );
};
